const colorRanges = {
  greys: {
    90: '#1B1F2D',
    70: '#676B7B',
    50: '#C4C7D1',
    30: '#EEEEEE',
    10: '#F6F6F9'
  },
  reds: {
    90: '#550909',
    70: '#CD1846',
    50: '#FF406E',
    30: '#FF6896',
    15: '#FFB3B3',
    10: '#FF90BE',
    0: '#FFB8E6'
  },
  greens: {
    90: '#007829',
    70: '#00A051',
    50: '#0CC879',
    30: '#34F0A1',
    10: '#5CFFC9',
    0: '#ECFBF4'
  },
  blues: {
    90: '#0B153C',
    70: '#322DCC',
    50: '#5A55F4',
    30: '#827DFF',
    15: '#C1BFFB',
    10: '#AAA5FF',
    0: '#F6F5FF'
  },
  yellows: {
    90: '#4B3100',
    15: '#FFE59F'
  }
}

const defaultColors = {
  primary: colorRanges.blues[50], // Blue
  secondary: colorRanges.reds[50], // Red
  green: colorRanges.greens[50],
  orange: '#FF5C00',
  red: colorRanges.reds[50],
  yellow: '#FFF8E2',
  white: '#FFF',
  black: '#000'
}

export type ColorState = {
  neutral: string
  hovered: string
}

/**
 * colorStates are the colors that have variations based on the state of the element using this color.
 *
 * This is not destructured as part of `genericColors` because some of these color such as green and orange
 * are not supposed to be available on the core of the `color` variable but to be assigned to a key in
 * `color` e.g. `color.state.success`.
 *
 * Only exporting this const for `determineColorForHover.ts`.
 * These colors should be fetched by theme.colors on any other file apart from `determineColorForHover.ts`
 */
export const colorStates: {
  primary: ColorState
  green: ColorState
  orange: ColorState
  red: ColorState
  yellow: ColorState
  grey: { [key: string]: ColorState }
  white: ColorState
} = {
  primary: {
    neutral: defaultColors.primary,
    hovered: colorRanges.blues[70]
  },
  green: {
    neutral: defaultColors.green,
    hovered: colorRanges.greens[70]
  },
  orange: {
    neutral: defaultColors.orange,
    hovered: '#EB4814'
  },
  red: {
    neutral: defaultColors.red,
    hovered: colorRanges.reds[70]
  },
  yellow: {
    neutral: defaultColors.yellow,
    hovered: '#F4F2AE'
  },
  grey: {
    70: {
      neutral: colorRanges.greys[70],
      hovered: colorRanges.greys[90]
    }
  },
  white: {
    neutral: defaultColors.white,
    hovered: colorRanges.greys[10]
  }
}

const colors = {
  ...colorRanges,
  primary: defaultColors.primary,
  white: defaultColors.white,
  black: defaultColors.black,
  error: defaultColors.red,
  red: {
    ...colorRanges.reds
  },
  text: {
    primary: colorRanges.greys[90],
    secondary: colorRanges.greys[70],
    tertiary: colorRanges.greys[50],
    black: defaultColors.black,
    placeholder: colorRanges.greys[70]
  },
  background: {
    primary: colorRanges.greys[10],
    secondary: colorRanges.greys[30],
    tertiary: colorRanges.blues[0]
  },
  border: {
    primary: colorRanges.greys[30],
    secondary: colorRanges.greys[50],
    tertiary: colorRanges.greys[70],
    yellow: colorRanges.yellows[90],
    blue: colorRanges.blues[90],
    red: colorRanges.reds[90]
  },
  shadow: defaultColors.primary,
  overlay: 'rgba(0, 0, 0, .25)',
  state: {
    error: defaultColors.red,
    success: defaultColors.green,
    disabled: colorRanges.greys[50]
  },
  icon: {
    primary: defaultColors.primary,
    darkGrey: colorRanges.greys[90],
    grey: colorRanges.greys[70],
    lightGrey: colorRanges.greys[50],
    white: defaultColors.white,
    success: defaultColors.green,
    error: defaultColors.red,
    alert: defaultColors.red,
    information: defaultColors.primary,
    warning: defaultColors.orange,
    gold: '#FFC108',
    darkYellow: colorRanges.yellows[90]
  },
  // TODO: See issue #4597
  snackbar: {
    success: defaultColors.green,
    information: defaultColors.primary,
    error: defaultColors.red,
    warning: defaultColors.orange
  },
  calendar: {
    shortTerm: {
      unavailable: {
        text: colorRanges.greys[50],
        background: colorRanges.greys[30]
      },
      past: {
        text: colorRanges.greys[30],
        background: defaultColors.white
      },
      available: {
        text: colorStates.white.neutral,
        background: colorRanges.greens['50'],
        border: 'transparent'
      },
      selected: {
        text: colorRanges.greens['50'],
        background: colorStates.white.neutral,
        border: colorRanges.greens['50']
      }
    },
    longTerm: {
      unavailable: {
        text: colorRanges.greys[50],
        background: colorRanges.greys[30]
      },
      past: {
        text: colorRanges.greys[30],
        background: defaultColors.white
      },
      available: {
        text: colorRanges.greys[70],
        background: colorStates.white.neutral,
        border: 'transparent'
      },
      selected: {
        text: colorStates.white.neutral,
        background: colorStates.primary.neutral,
        border: 'transparent'
      }
    }
  },
  styleguide: {
    border: colorRanges.greys[10]
  },
  status: {
    active: { background: '#ECFBF4', text: colorRanges.greens[50] },
    disabled: { background: '#ECFBF4', text: colorRanges.greens[50] },
    inactive: {
      background: colorRanges.greys[30],
      text: colorRanges.greys[70]
    },
    warning: {
      background: '#FFD9E0',
      text: colorRanges.reds[70]
    }
  },
  tabs: {
    tabList: {
      background: colorRanges.greys[10]
    }
  },
  eola: {
    greys: colorRanges.greys
  },
  formHeader: {
    red: {
      background: colorRanges.reds[15],
      text: colorRanges.reds[90]
    },
    yellow: {
      background: colorRanges.yellows[15],
      text: colorRanges.yellows[90]
    },
    blue: {
      background: colorRanges.blues[15],
      text: colorRanges.blues[90]
    }
  },
  signUpSidePanel: {
    yellow: {
      background: colorRanges.yellows[15],
      text: colorRanges.yellows[90]
    },
    red: {
      background: colorRanges.reds[15],
      text: colorRanges.reds[90]
    },
    blue: {
      background: colorRanges.blues[15],
      text: colorRanges.blues[90]
    }
  }
}

export default colors
